import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {isDefined} from '../../commons/commons';
import {ECMode} from 'smag-commons/cfg/hierarchy';
import {BehaviorSubject, interval, Observable, Subject} from 'rxjs';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {map, switchMap, takeUntil} from 'rxjs/operators';
import {I18NService} from '../../i18n/i18n.service';

export interface EnergyCommunityInfo {
    mode: ECMode;
}

export interface ECAggregation {
    name: string;
    nameArgs?: any[];
    valueProvider: ValueProvider;
    uomProvider: UomProvider;
}

export type ValueProvider = (timeRange: [number, number]) => Observable<number>; // duration in minutes
export type UomProvider = () => Observable<string>;

enum TimeRange {
    LAST_HOUR,
    LAST_24_HOURS,
    LAST_7_DAYS,
    LAST_30_DAYS,
    LAST_60_DAYS
}

interface AggregatedData {
    name: string;
    aggregatedValue: Observable<{ value: number }>;
    uom?: Observable<string>;
}

const DEFAULT_TIME_RANGE: TimeRange = TimeRange.LAST_HOUR;

@Component({
    selector: 'app-ec-measure-aggregations',
    templateUrl: './ec-measure-aggregations.component.html',
    styleUrls: ['./ec-measure-aggregations.component.scss']
})
export class ECMeasureAggregationsComponent implements OnInit, OnChanges, OnDestroy {

    private destroyed = new Subject<void>();
    private aggregationRefreshSubj: BehaviorSubject<Moment>;

    timeRange: TimeRange;
    aggregatedData: AggregatedData[];

    @Input() ecInfo: EnergyCommunityInfo;
    @Input() aggregations: ECAggregation[];
    @Input() refreshFrequency: number; // in seconds (0 = never refresh)

    // exported in HTML
    isDefined = isDefined;
    ECMode = ECMode;
    TimeRange = TimeRange;

    constructor(private readonly i18n: I18NService) {
        this.aggregationRefreshSubj = new BehaviorSubject<Moment>(moment());
        this.timeRange = DEFAULT_TIME_RANGE;
        this.aggregatedData = [];
    }

    ngOnInit() {
        if (!!this.refreshFrequency) {
            interval(this.refreshFrequency * 1000).pipe(
                takeUntil(this.destroyed)
            ).subscribe(() => {
                this.aggregationRefreshSubj.next(moment());
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('aggregations' in changes) {
            this.mkAggregations();
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get aggregationReferenceTime(): Moment {
        return this.aggregationRefreshSubj.value;
    }

    get ecModeIcon(): string {
        switch (this.ecInfo?.mode) {
            case ECMode.BASE:
                return 'fal-chart-network';
            case ECMode.ADVANCED:
                return 'fal-radar';
            case ECMode.HYBRID:
                return 'fal-shapes';
            default:
                return null;
        }
    }

    trackByIndex(index: number, _: AggregatedData): number {
        return index;
    }

    onSelectTimeRange(timeRange: TimeRange) {
        if (timeRange !== this.timeRange) {
            this.timeRange = timeRange;
            this.mkAggregations();
        }
    }

    private mkAggregations() {
        const to = this.aggregationRefreshSubj.value.valueOf();
        const from = to - mkDuration(this.timeRange) * 60000;
        this.aggregatedData = this.aggregations.map(agg => ({
            name: this.i18n.translate(agg.name, ...(agg.nameArgs || [])),
            aggregatedValue: this.aggregationRefreshSubj.pipe(
                switchMap(() => agg.valueProvider([from, to])),
                map(value => ({value}))
            ),
            uom: agg.uomProvider()
        } as AggregatedData));
    }
}

function mkDuration(timeRange: TimeRange): number {
    switch (timeRange) {
        case TimeRange.LAST_HOUR:
            return 60;
        case TimeRange.LAST_24_HOURS:
            return 24 * 60;
        case TimeRange.LAST_7_DAYS:
            return 7 * 24 * 60;
        case TimeRange.LAST_30_DAYS:
            return 30 * 24 * 60;
        case TimeRange.LAST_60_DAYS:
            return 60 * 24 * 60;
        default:
            return 0;
    }
}
